// Tested by __tests__/RunTrace.test.tsx (0064: the saved-pass panel's act re-mounts the trace section on a fresh read of the run, and the live trace subscribes again). The rest of this file (a) renders the in-flight live-status indicator, (b) renders the mirror-reducer patch-error panel, (c) renders the trace tree via TraceBody; RunErrorsPanel (exported for its test) is tested by RunErrorsPanel.test.tsx, the mirror reducer by traceMirrorReducer.test.ts, the saved-pass panel by SavedPassPanel.test.tsx.
import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useUser } from "../auth/useUser";
import { Header } from "../components/Header";
import { SavedPassPanel } from "../components/SavedPassPanel";
import { fetchRunTrace } from "../lib/traceApi";
import type { RunTrace } from "../lib/traceApi";
import { useLiveTrace } from "../hooks/useLiveTrace";
import { StageRow } from "../components/trace-tree/StageRow";
import { RunOutliers } from "../components/trace-tree/RunOutliers";
import { TraceFilterContext } from "../components/trace-tree/TraceFilterContext";
import { TraceLiveContext } from "../components/trace-tree/TraceLiveContext";
import {
  TraceCompareContext,
  buildCompareState,
} from "../components/trace-tree/TraceCompareContext";
import {
  parseAnchor,
  TRACE_ANCHOR_FOCUS_EVENT,
} from "../lib/traceAnchors";

// V2 admin run-trace view per plan sec 7.5 + sec 13.1.
// Primary admin run surface — replaces the legacy RunDetail.tsx (deleted).
// Route: /users/:uid/products/:pid/runs/:rid
// V1 historical runs (trace==null) degrade gracefully to a RunRecord JSON
// dump via TraceBody's null-trace branch.
//
// Admin UX additions (plan §7.8):
//   §7.8.2 Per-attempt diff view              → ExpandPanel
//   §7.8.3 Cost + duration outliers           → RunOutliers (rendered below)
//   §7.8.4 Error timeline (errorsOnly toggle) → already wired through rows
//   §7.8.8 Permalink + share                  → traceAnchors + hash-on-mount
//   §7.8.9 Bridge mutation detail panel       → BridgeStepRow
//
// 0064: a saved pass (record status paused) shows SavedPassPanel above the
// trace, with Resume and Close and refund. After either act the trace
// section re-mounts on a fresh read of the run, so useLiveTrace subscribes
// again (a resumed pass streams its new segment).
export function RunTraceView() {
  const { uid = "", pid = "", rid = "" } = useParams();
  const { data: me } = useUser();
  const queryClient = useQueryClient();
  const [traceGeneration, setTraceGeneration] = useState(0);

  if (!me) return null;

  const onSavedPassAct = () => {
    queryClient.removeQueries({ queryKey: ["admin", "run-trace", uid, pid, rid] });
    queryClient.invalidateQueries({ queryKey: ["admin", "saved-pass", uid, pid, rid] });
    queryClient.invalidateQueries({ queryKey: ["admin", "paused-runs"] });
    setTraceGeneration((g) => g + 1);
  };

  return (
    <div className="min-h-screen flex flex-col">
      <Header user={me} />
      <main className="flex-1 max-w-6xl mx-auto w-full px-4 sm:px-8 py-10">
        <Link
          to={`/users/${uid}/products/${pid}`}
          className="font-mono text-[11px] text-ink-400 hover:text-ink-100"
        >
          ← Product
        </Link>
        <h1 className="mt-4 text-display font-medium text-ink-50 tracking-tightest mb-2">
          Run detail
        </h1>
        <p className="font-mono text-[11px] text-ink-400 mb-6">
          {uid} / {pid} / {rid}
        </p>
        <RunTraceSection
          key={`${rid}:${traceGeneration}`}
          uid={uid}
          pid={pid}
          rid={rid}
          onSavedPassAct={onSavedPassAct}
        />
      </main>
    </div>
  );
}

// RunTraceSection is the run page's trace: the saved-pass panel (0064), the
// live-status line, the patch-error panel and the trace tree. RunTraceView
// re-mounts it (its key) after a saved-pass act.
function RunTraceSection({
  uid,
  pid,
  rid,
  onSavedPassAct,
}: {
  uid: string;
  pid: string;
  rid: string;
  onSavedPassAct: () => void;
}) {
  const [errorsOnly, setErrorsOnly] = useState(false);
  const [filter, setFilter] = useState("");
  const [referenceRunId, setReferenceRunId] = useState("");

  // Reference run for compare mode (item 6 / plan §7.8.5). Fetched only
  // when the operator pins a reference run via the picker.
  const referenceQ = useQuery({
    queryKey: ["admin", "run-trace-ref", uid, pid, referenceRunId],
    queryFn: () => fetchRunTrace(uid, pid, referenceRunId),
    enabled: Boolean(uid && pid && referenceRunId && referenceRunId !== rid),
  });
  const compareState = buildCompareState(
    referenceRunId,
    (referenceQ.data?.trace as RunTrace | undefined) ?? null,
  );

  // Unified live + post-hoc trace source. useLiveTrace runs the SSE
  // subscription + 0062 mirror reducer during the in-flight window
  // (consuming trace_snapshot anchors + trace_patch deltas) and seeds
  // from the persisted query result at run-end. Single trace source;
  // tree components render identically regardless of which pathway
  // populated state.
  const live = useLiveTrace(uid, pid, rid);
  const trace = live.trace;
  const runRecord = live.runRecord;

  // Hash-based deep link (plan §7.8.8). When the trace finishes loading,
  // check window.location.hash and dispatch a focus event the matching row
  // will pick up to auto-expand + scroll itself into view. We dispatch on
  // every trace change so refetches that bring in additional rows
  // (in-flight live events) still home in on the targeted row if the user
  // navigated to it before it existed.
  useEffect(() => {
    if (!trace) return;
    const anchor = parseAnchor(window.location.hash);
    if (!anchor) return;
    requestAnimationFrame(() => {
      window.dispatchEvent(
        new CustomEvent(TRACE_ANCHOR_FOCUS_EVENT, { detail: anchor.raw }),
      );
      const el = document.getElementById(anchor.raw);
      el?.scrollIntoView({ behavior: "smooth", block: "center" });
    });
  }, [trace]);

  const runStatus = live.runStatus;
  const isInFlight = runStatus === "running" || runStatus === "";

  return (
    <>
        <SavedPassPanel
          uid={uid}
          pid={pid}
          rid={rid}
          status={runStatus}
          onActed={onSavedPassAct}
        />
        {isInFlight && (
          <div className="mb-3 flex items-center gap-3 text-xs font-mono text-ink-400">
            <LiveStatusDot status={live.liveStatus} />
            <span>
              Live trace · {live.source === "live" ? "in-flight" : "connecting"}
            </span>
            <span className="text-ink-500">
              ({live.liveStatus})
            </span>
          </div>
        )}
        {/* 0062: patch-apply failures surface here. Empty array in the
            happy path; non-empty signals server-side patch construction
            and frontend state drifted. */}
        {live.patchErrors.length > 0 && (
          <div className="mb-3 px-3 py-2 rounded border border-amber-500/40 bg-amber-900/20 text-xs font-mono text-amber-200">
            <div className="font-semibold mb-1">
              mirror patch errors ({live.patchErrors.length}):
            </div>
            <ul className="space-y-0.5">
              {live.patchErrors.slice(-5).map((msg, i) => (
                <li key={i} className="text-amber-300/90">{msg}</li>
              ))}
            </ul>
          </div>
        )}
        {!trace && !isInFlight && (
          <p className="text-sm text-ink-400">loading…</p>
        )}
        {trace && (
          <TraceFilterContext.Provider value={{ query: filter }}>
            <TraceCompareContext.Provider value={compareState}>
              <TraceLiveContext.Provider
                value={{
                  // Live = the mirror reducer is the source. useLiveTrace
                  // flips source to "persisted" the instant the run ends
                  // (runEnded set by run_complete / run_error SSE, or by
                  // terminal-at-mount), so source==="live" precisely means
                  // "in-flight, reducer authoritative." We deliberately do
                  // NOT also gate on trace.completed_at: that field is
                  // server-derived and was briefly non-nil mid-run (early
                  // synthetic stages seal first); relying on it conflated
                  // "a stage completed" with "the run completed." source is
                  // the authoritative liveness signal.
                  live: live.source === "live",
                }}
              >
              <TraceBody
                data={{ trace, run: runRecord }}
                errorsOnly={errorsOnly}
                onToggle={() => setErrorsOnly((v) => !v)}
                filter={filter}
                onFilterChange={setFilter}
                referenceRunId={referenceRunId}
                onReferenceRunIdChange={setReferenceRunId}
                referenceLoading={referenceQ.isLoading}
                referenceError={
                  referenceQ.isError ? String(referenceQ.error) : ""
                }
              />
              </TraceLiveContext.Provider>
            </TraceCompareContext.Provider>
          </TraceFilterContext.Provider>
        )}
    </>
  );
}

// LiveStatusDot mirrors the connection-state indicator that LiveTraceStream
// rendered as a bullet in its own panel header. Now lives inline above the
// trace tree because the tree IS the live view (V2 plan §7.5 consolidation).
function LiveStatusDot({ status }: { status: "connecting" | "open" | "closed" | "error" }) {
  const color =
    status === "open"
      ? "text-green-400"
      : status === "closed"
        ? "text-gray-400"
        : status === "error"
          ? "text-amber-400"
          : "text-blue-400";
  return <span className={color}>●</span>;
}

function TraceBody({
  data,
  errorsOnly,
  onToggle,
  filter,
  onFilterChange,
  referenceRunId,
  onReferenceRunIdChange,
  referenceLoading,
  referenceError,
}: {
  data: { trace: RunTrace | null; run: unknown };
  errorsOnly: boolean;
  onToggle: () => void;
  filter: string;
  onFilterChange: (q: string) => void;
  referenceRunId: string;
  onReferenceRunIdChange: (rid: string) => void;
  referenceLoading: boolean;
  referenceError: string;
}) {
  if (!data.trace) {
    return (
      <div className="text-gray-400">
        This run has no V2 trace recorded. (V1 run, panic-recovery path, or pre-trace-feature run.)
        <pre className="mt-4 text-xs bg-gray-900 p-2 overflow-auto">{JSON.stringify(data.run, null, 2)}</pre>
      </div>
    );
  }
  const trace = data.trace;
  // Defensive `?? []`: Firestore Go SDK round-trips empty slices to nil →
  // JSON null → frontend crash on `.flatMap()`. Backend normalizes too;
  // this is belt-and-suspenders.
  //
  // Skip legacy "ghost" stages: a stage with outcome="" AND zero layers is
  // an unsealed BeginStage that never paired with EndStage. Pre-c7ded49
  // runs persisted these because of a double-BeginStage bug in the run loop
  // (fixed at the source for new runs; this filter cleans the rendering of
  // existing persisted traces so operators don't have to ignore the row).
  const stages = (trace.stages ?? []).filter(
    (s) => !(s.outcome === "" && (s.layers ?? []).length === 0),
  );
  const errors = trace.errors ?? [];
  const totalSubCalls = stages
    .flatMap((s) => (s.layers ?? []).flatMap((l) => l.sub_calls ?? [])).length;
  const durationMs = trace.completed_at
    ? new Date(trace.completed_at).getTime() - new Date(trace.started_at).getTime()
    : Date.now() - new Date(trace.started_at).getTime();
  const totalCost = stages.reduce((s, st) => s + (st.total_cost_usd ?? 0), 0);
  const totalInTok = stages.reduce((s, st) => s + (st.total_input_tokens ?? 0), 0);
  const totalOutTok = stages.reduce((s, st) => s + (st.total_output_tokens ?? 0), 0);

  // Filtered trace for the error timeline view (§7.8.4). We only pass this
  // to RunOutliers when errorsOnly is on so the outliers panel reflects
  // failed work — useful when triaging "what cost us before the abort".
  const outlierTrace: RunTrace = errorsOnly
    ? { ...trace, stages: filterErrorStages(stages) }
    : { ...trace, stages };

  return (
    <>
      <div className="border border-gray-700 rounded p-3 bg-gray-900 mb-3">
        <div className="flex items-center gap-4 text-sm">
          <span>Run {trace.run_id}</span>
          <span className="text-gray-400">{(durationMs / 1000).toFixed(1)}s</span>
          <span className="text-gray-400">
            {stages.length} stages · {totalSubCalls} sub-calls
          </span>
          <span className="text-gray-400">
            {totalInTok}→{totalOutTok} tok · ${totalCost.toFixed(2)}
          </span>
          <span className={errors.length > 0 ? "text-red-400" : "text-green-400"}>
            {errors.length} errors
          </span>
        </div>
        <div className="mt-2 flex items-center gap-2">
          <button
            onClick={onToggle}
            className={`px-2 py-1 text-xs border rounded ${errorsOnly ? "bg-red-900 border-red-400" : "border-gray-600"}`}
          >
            {errorsOnly ? "Show all" : "Errors only"}
          </button>
          <input
            type="text"
            value={filter}
            onChange={(e) => onFilterChange(e.target.value)}
            placeholder="filter rows by sub_prompt_id / item_key / step_id / error text…"
            className="flex-1 bg-gray-950 border border-gray-700 text-xs px-2 py-1 rounded text-ink-100 placeholder-gray-500"
          />
          {filter && (
            <button
              onClick={() => onFilterChange("")}
              className="text-xs text-gray-400 hover:text-gray-200"
            >
              clear
            </button>
          )}
        </div>
        {/* Compare-mode picker (item 6 / plan §7.8.5). Pin a reference
            run by ID; divergent sub-calls get a △ badge + side-by-side
            diff in their ExpandPanel. */}
        <div className="mt-2 flex items-center gap-2">
          <label className="text-xs text-gray-400 font-mono">Compare to run:</label>
          <input
            type="text"
            value={referenceRunId}
            onChange={(e) => onReferenceRunIdChange(e.target.value)}
            placeholder="paste reference run_id (e.g. 2026-05-19-abc123)"
            className="flex-1 bg-gray-950 border border-gray-700 text-xs px-2 py-1 rounded text-ink-100 placeholder-gray-500"
          />
          {referenceLoading && <span className="text-xs text-gray-400">loading…</span>}
          {referenceError && (
            <span className="text-xs text-rose-300" title={referenceError}>load failed</span>
          )}
          {referenceRunId && (
            <button
              onClick={() => onReferenceRunIdChange("")}
              className="text-xs text-gray-400 hover:text-gray-200"
            >
              unpin
            </button>
          )}
        </div>
      </div>
      <RunErrorsPanel errors={errors} />
      <RunOutliers trace={outlierTrace} />
      <div className="space-y-1">
        {stages.map((stage, i) => (
          <StageRow
            key={i}
            stage={stage}
            errorsOnly={errorsOnly}
            errors={errors}
          />
        ))}
        <SkippedStagesNotice
          stageNames={stages.map((s) => s.stage)}
          runComplete={trace.completed_at != null}
        />
      </div>
    </>
  );
}

// RunErrorsPanel: top-level visible-on-page-load surface for every entry
// in RunTrace.Errors[]. Renders the full list grouped by stage so an
// operator opening the run for a "what failed?" answer sees it before
// expanding any row. Previously this was just an "{N} errors" count in
// the run header — the user opens a sub-call expecting "Validation"
// to tell them why, finds it empty (because per-call validation passed),
// and concludes "I don't see what failed." The data was always there;
// nothing was surfacing it at the right scope.
export function RunErrorsPanel({ errors }: { errors: import("../lib/traceApi").TraceError[] }) {
  if (errors.length === 0) return null;
  // Group by stage so the failure cluster is obvious; within each stage
  // the order matches the order trace.errors[] was emitted (chronological).
  const byStage = new Map<string, import("../lib/traceApi").TraceError[]>();
  for (const e of errors) {
    const arr = byStage.get(e.stage) ?? [];
    arr.push(e);
    byStage.set(e.stage, arr);
  }
  return (
    <div className="border border-rose-700 bg-rose-950/30 rounded p-3 mb-3">
      <div className="flex items-center gap-2 mb-2">
        <span className="text-rose-300 font-bold">{errors.length} run-trace error{errors.length === 1 ? "" : "s"}</span>
        <span className="text-rose-200/70 text-xs">
          (post-DAG validators, ApplyCreateDelta, supersession/coverage_claim applies — failures that live OUTSIDE the per-sub-call scope)
        </span>
      </div>
      <div className="space-y-2">
        {Array.from(byStage.entries()).map(([stage, list]) => (
          <div key={stage}>
            <div className="text-rose-400 font-mono text-xs mb-1">Stage: {stage}</div>
            <ul className="space-y-1">
              {list.map((e, i) => (
                <li
                  key={i}
                  className="text-xs font-mono bg-rose-950/50 border-l-2 border-rose-600 pl-2 py-1"
                >
                  <span className="text-rose-400">[{e.source}/{e.code}]</span>{" "}
                  <span className="text-rose-100">{e.detail}</span>
                </li>
              ))}
            </ul>
          </div>
        ))}
      </div>
    </div>
  );
}

// filterErrorStages narrows a stages list to only those carrying failed
// work, for the §7.8.4 error timeline view. A stage qualifies if:
//   - its own outcome is not "success" (covers aborts/panics), OR
//   - any nested layer has a failed sub-call (outcome !== "success"), OR
//   - its bridge aborted.
// We mirror the same predicate StageRow/LayerRow already use for hiding so
// the outlier list stays consistent with what's visible in the tree.
function filterErrorStages(
  stages: RunTrace["stages"],
): RunTrace["stages"] {
  return stages.filter((stage) => {
    if (stage.outcome !== "success") return true;
    const layers = stage.layers ?? [];
    return layers.some((l) => {
      const subFailed = (l.sub_calls ?? []).some((sc) => sc.outcome !== "success");
      const bridgeFailed = l.bridge?.outcome === "abort";
      return subFailed || bridgeFailed;
    });
  });
}

// SkippedStagesNotice renders a row per canonical V2 stage (analyze/decide/
// create) that is missing from the trace's stages slice. A run that produces
// 0 gaps in ANALYZE skips DECIDE; an ANALYZE no-op skips both DECIDE and
// CREATE. Without this row the admin sees a partial pipeline with no signal
// that work was deliberately skipped (which looks like "trace persistence
// dropped data" — different debugging path).
//
// runComplete gates the post-hoc inference: during a live run, missing
// stages are queued (haven't fired yet), not skipped. Only after the run
// terminates (trace.completed_at set, either via run_complete/run_error
// or via swap to persisted) does "missing == skipped" hold.
function SkippedStagesNotice({
  stageNames,
  runComplete,
}: {
  stageNames: string[];
  runComplete: boolean;
}) {
  if (!runComplete) return null;
  const canonical = ["analyze", "decide", "create"] as const;
  const present = new Set(stageNames);
  const missing = canonical.filter((s) => !present.has(s));
  if (missing.length === 0) return null;
  return (
    <div className="border-l-2 border-amber-600 pl-2 mt-2">
      <div className="flex items-center gap-2 px-2 py-1 text-sm text-amber-300">
        <span className="w-16 text-xs font-mono">SKIPPED</span>
        <span className="font-mono font-bold">{missing.join(", ")}</span>
        <span className="text-amber-400/70 text-xs ml-2">
          stage{missing.length > 1 ? "s" : ""} did not run (check run.summary for the reason — typically "no new gaps" means DECIDE/CREATE skipped intentionally)
        </span>
      </div>
    </div>
  );
}
